/**
 * Credit Note Template for Thailand
 * only for October 2023 as a result of TH government announcement
 * to reduce FT cost from 0.6689 to 0.2048 baht/kWh starting from Steptermber 2023
 */

import React, { useState, useMemo } from "react";
import { useFormatter } from "./../../../util/formatter";
import LoaderGraph from "../../general/LoaderGraph";
import fonts_config from "./fonts_config.json";
import pdfFonts from "pdfmake/build/vfs_fonts.js";
import pdfMake from "pdfmake/build/pdfmake.js";
import { Button, Row } from "react-bootstrap";
import { useSolarCalc } from "../../../util/calculations";
import GYLogo from "../../../images/BASE64/GYLogo";
import accSignature from "../../../images/BASE64/accSignature";
import usePeakLabels from "../../../util/peakLabels";
import {
	getElectricBillSection,
	getBTGElectricBillSection,
} from "./ElectricBillSection";
import moment from "moment-timezone";
import GYAxios from "../../../util/GYAxios";
import rfdc from 'rfdc';

// correct ft cost is 0.2048
const ft_cost_corr = 0.2048;

const clone = rfdc();

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfMake.vfs;
pdfMake.fonts = fonts_config;

// convert centimeters to points (PDFMake use Point as a unit)
const cm_to_pt = cm => cm * 28.346;

const invNoToCreditNoteNo = invNo => {
	let creditNoteNo = invNo.replace(/^INV-/i, 'CN-');
	creditNoteNo = creditNoteNo.replace(/-2309-/i, '-2310-');
	return creditNoteNo;
}

function CreditNoteTemplate_TH({ copyType='ORIGINAL', onDocDefinition=()=>{}, ...props }) {
	const user = props.user;
	const [formatNumber] = useFormatter();
	const solarCalc = useSolarCalc();
	const parseNo = solarCalc.parseFormatNumber;
	const [PDF, setPDF] = useState(null);
	const [PDFDoc, setPDFDoc] = useState(null);
	const [creditNotePDFName, setCreditNotePDFName] = useState(null);
	const logo = GYLogo;
	const signImg = accSignature;

	const selectedSite = props.selectedSite;
	const country = selectedSite.value.country;
	const peakLabels = usePeakLabels(country);

	const copyTypes = useMemo(() => ({
		ORIGINAL: "Original (ต้นฉบับ)",
		COPY: 'Copy (สำเนา)'
	}), []);

	// Show signature for client access
	const showSignature = user.roles.includes(
		"administration:InvoiceTable:_clientViewInv"
	);

	// Signature object
	const signObj = {
		image: signImg,
		height: 50,
		width: 107,
		absolutePosition: { x: -300, y: -145 },
	};

	// Signature section object
	const signature = showSignature ? signObj : {};

	/**
	 * Get discount remark in language requested
	 * @param {Language} lang
	 * @param {Discount} discount
	 * @param {MEA/PEA} EAtariff
	 */
	const getDiscountRemark = (lang, discount, EAtariff) => {
		switch (lang) {
			case "TH":
				return ` (ส่วนลดอัตราค่าไฟ ${parseFloat(discount)}% จาก ${EAtariff})`;

			default:
				return ` (with ${parseFloat(discount)}% discount from ${EAtariff}'s Tariff)`;
		}
	};

	/**
	 * Get Tariff information in English and Thai language
	 * @param {Parameters of site} gyTariffParam
	 * @param {MEA/PEA} EAtariff
	 */
	const getGYTariff = (gyTariffParam, EAtariff, isBTGSite) => {
		// Decimal place for BTG sites
		const dec = isBTGSite ? 4 : 6;

		// On-Peak information
		const priceOn = gyTariffParam.priceON_disc.toFixed(dec);
		let ONpeakStr_eng = `${peakLabels[0]} (baht/kWh) = ${priceOn}`;
		let ONpeakStr_th = `ช่วงเวลา ${peakLabels[0]} (บาท/kWh) = ${priceOn}`;

		// Site has discount of On-Peak
		if (gyTariffParam.discountON > 0) {
			const discONPercent = formatNumber(gyTariffParam.discountON * 100, 2);
			ONpeakStr_eng += getDiscountRemark("ENG", discONPercent, EAtariff[0]);
			ONpeakStr_th += getDiscountRemark("TH", discONPercent, EAtariff[1]);
		}

		// Off-peak information
		const priceOff = gyTariffParam.priceOFF_disc.toFixed(dec);
		let OFFpeakStr_eng = `${peakLabels[1]} (baht/kWh) = ${priceOff}`;
		let OFFpeakStr_th = `ช่วงเวลา ${peakLabels[1]} (บาท/kWh) = ${priceOff}`;

		// Site has discount of Off-peak
		if (gyTariffParam.discountOFF > 0) {
			const discOFFPercent = formatNumber(gyTariffParam.discountOFF * 100, 2);
			OFFpeakStr_eng += getDiscountRemark("ENG", discOFFPercent, EAtariff[0]);
			OFFpeakStr_th += getDiscountRemark("TH", discOFFPercent, EAtariff[1]);
		}

		// FT cost information
		let FTStr_eng = "";
		let FTStr_th = "";

		// Site has discount of FT cost
		if (gyTariffParam.discountFT > 0) {
			const priceFT = gyTariffParam.FTcost_disc.toFixed(dec);
			FTStr_eng = `FT (baht/kWh) = ${priceFT}`;
			FTStr_th = `FT (บาท/kWh) = ${priceFT}`;

			const discFTPercent = formatNumber(gyTariffParam.discountFT * 100, 2);
			FTStr_eng += getDiscountRemark("ENG", discFTPercent, EAtariff[0]);
			FTStr_th += getDiscountRemark("TH", discFTPercent, EAtariff[1]);
		}

		return {
			ONpeakStr_eng,
			ONpeakStr_th,
			OFFpeakStr_eng,
			OFFpeakStr_th,
			FTStr_eng,
			FTStr_th,
		};
	};

	/**
	 * Set PDF name to be downloaded
	 * @param {Selected site} site
	 */
	const getCreditNotePDFName = (site) => {
		const pdfName = `${site.siteCode}_credit_note_2023-09`;
		return pdfName;
	};

	/**
	 * Get discount text
	 */
	const getDiscountText = (discount) =>
		`- Discount ${parseFloat(formatNumber(discount * 100, 2))}%`;

	/**
	 * Build location object
	 * @param {location string array} location
	 * @returns location object
	 */
	const buildLocationSection = (location) => {
		if (location != "")
				return [
					[
						{
							text: "Location:",
							style: ["invoice_details", { bold: true }],
						},
						{ text: location[0], style: ["invoice_details"] },
					],
					[
						{
							colSpan: 2,
							text: location[1] || '',
							style: ["invoice_details"],
						},
						"",
					],
				];
		else
			return [
				[
					{
						text: "",
					},
					{ text: "" },
				],
				[
					{
						colSpan: 2,
						text: "",
					},
					"",
				],
			];
	};

	/**
	 * Build entity section object
	 * @param {Solar entity object} solarEntity
	 * @returns Entity section object
	 */
	const buildEntitySection = (solarEntity, options={ isNewInv: false }) => {
		const addressArr = solarEntity.address.split("_");
		const addressRows = addressArr.map((rows) => ({ text: rows }));

		const section = [
			{
				text: solarEntity.name,
				style: ["boldHeader"],
			},
			...addressRows,
			{
				text: `TAX ID : ${solarEntity.taxID}`,
			},
			{
				text: `Contact person : ${!options.isNewInv ? solarEntity.accountantName : "Accounting"}`,
			},
			{
				text: `Tel: ${solarEntity.telephone}`,
			},
		];

		return section;
	};

	/**
	 * Build payment section object
	 * @param {Bank account obj} bankAccount
	 * @returns Payment section obj
	 */
	const buildPaymentSection = (bankAccount) => {
		const accNumber = bankAccount.accNumber;
		const accNumRemark = bankAccount.accNumRemark;

		let accountNumbers = accNumber != null ? accNumber.split(",") : ["-"];
		let accountRemarks = accNumRemark != null ? accNumRemark.split(",") : ["-"];

		const accName = bankAccount.accName != null ? bankAccount.accName : "-";
		const bankName = bankAccount.bankName != null ? bankAccount.bankName : "-";
		const branch = bankAccount.branch != null ? bankAccount.branch : "-";
		const swiftCode =
			bankAccount.swiftCode != null ? bankAccount.swiftCode : "-";

		let accNumSection = [];

		if (accountNumbers.length === accountRemarks.length) {
			accountNumbers = accountNumbers.map((str) => str.trim());
			accountRemarks = accountRemarks.map((str) => str.trim());

			accNumSection.push("Beneficiary Account number : ");

			for (let i = 0; i < accountNumbers.length; i++) {
				const accNumberStr = { text: accountNumbers[i], style: "boldHeader" };
				const accRemarkStr = { text: ` [${accountRemarks[i]}]  ` };

				accNumSection.push(accNumberStr);
				accNumSection.push(accRemarkStr);
			}
		}

		const section = [
			{
				text: [
					"Please make the payment to ",
					{
						text: accName,
						style: "boldHeader",
					},
				],
			},
			{
				text: [
					"Bank : ",
					{ text: `${bankName}, `, style: "boldHeader" },
					"Branch : ",
					{ text: branch, style: "boldHeader" },
				],
			},
			{
				text: accNumSection,
			},
			{
				text: ["Bank Swift Code : ", { text: swiftCode, style: "boldHeader" }],
			},
		];

		return section;
	};

	/**
	 * Generate invoice
	 * @param {Selected Site} selectedSite
	 */
	const generateInvoice = async (selectedSite) => {
		const data = selectedSite.value;
		const creditNoteNo = invNoToCreditNoteNo(data.invNo);
		const holidays =
			selectedSite.holidays != "" && selectedSite.holidays != null
				? selectedSite.holidays
				: "";

		const selectedDate = moment(data.date, "DD/MM/YYYY", true)
		const endMonth = moment(selectedDate).endOf('month')
		const startMonth = moment(selectedDate).startOf('month')
		const _cod = moment(data.cod)
		const diff = startMonth.diff(_cod, "years", true)
		const period = (diff > 0 ? startMonth.format("DD/MM/YYYY") : _cod.format("DD/MM/YYYY")) + " - " + endMonth.format("DD/MM/YYYY")

		const location = data.location.split("_");
		const billingName = data.billingName != null ? data.billingName : "-";
		const billingAddress =
			data.billingAddress != null ? data.billingAddress : "-";

		const isBTGSite = data.clientID == "69";
		const solarEntity = data.solarEntity;
		const bankAccount = data.bankAccount;

		// const tempEntityName = solarEntity.name;
		// const authName = tempEntityName.replace(" (Head Office)", "");

		// const tariffData = data.tariffData;
		// const tariffID = tariffData.tariffID.split("/")[0];
		// const tariffType = tariffID.split("_");
		// const tariffDesc = tariffData.tariffDesc;
		// const EAEng = tariffData.EAEng.split("_");
		// const EAThai = tariffData.EAThai.split("_");

		// Set PDF document name
		setCreditNotePDFName(getCreditNotePDFName(data));

		// Solar Production values
		const solarON = formatNumber(data.solarON, 3);
		const solarOFF = formatNumber(data.solarOFF, 3);
		const solarTotal = formatNumber(data.solarTotal, 3);

		// Discount text
		const discountON_display =
			data.discountON > 0 ? getDiscountText(data.discountON) : "";
		const discountOFF_display =
			data.discountOFF > 0 ? getDiscountText(data.discountOFF) : "";
		const discountFT_display =
			data.discountFT > 0 ? getDiscountText(data.discountFT) : "";

		// Special decimal place for unit price calculations
		const specialDP = isBTGSite ? 4 : null;

		// Discounted Price
		const priceON_disc = solarCalc.calculateUnitCost(
			data.priceON,
			data.discountON,
			data.currency,
			specialDP
		);
		const priceOFF_disc = solarCalc.calculateUnitCost(
			data.priceOFF,
			data.discountOFF,
			data.currency,
			specialDP
		);
		const FTcost_disc = solarCalc.calculateUnitCost(
			data.FTcost,
			data.discountFT,
			data.currency,
			specialDP
		);
		const FTcost_disc_corr = solarCalc.calculateUnitCost(
			ft_cost_corr, // correct FT cost for Steptember 2023
			data.discountFT,
			data.currency,
			specialDP
		);

		// // Tariff section
		// const gyTariffParam = {
		// 	discountON: data.discountON,
		// 	discountOFF: data.discountOFF,
		// 	discountFT: data.discountFT,
		// 	priceON_disc,
		// 	priceOFF_disc,
		// 	FTcost_disc,
		// };

		// const GYTariff_remarks = getGYTariff(
		// 	gyTariffParam,
		// 	[EAEng[1], EAThai[1]],
		// 	isBTGSite
		// );

		// Savings and Cost calculations
		const prod = {
			ON: data.solarON,
			OFF: data.solarOFF,
			TOTAL: data.solarTotal,
		};
		const price = {
			ON: data.priceON,
			OFF: data.priceOFF,
			FT: data.FTcost,
		};
		const price_corr = {
			ON: data.priceON,
			OFF: data.priceOFF,
			FT: ft_cost_corr,
		};
		const discount = {
			ON: data.discountON,
			OFF: data.discountOFF,
			FT: data.discountFT,
		};

		// Savings and cost calculations results
		// const savingsObj = solarCalc.calculateSavings(
		// 	prod,
		// 	price,
		// 	discount,
		// 	data.currency
		// );
		const costsObj = solarCalc.calculateProdCost(
			prod,
			price,
			discount,
			data.currency,
			specialDP
		);
		const cocstsObj_corr = solarCalc.calculateProdCost(
			prod,
			price_corr,
			discount,
			data.currency,
			specialDP
		);

		// Savings
		// const savings = savingsObj.savingsTotal;

		// Prices
		const totalPriceON = costsObj.costON;
		const totalPriceOFF = costsObj.costOFF;
		const totalPriceFT = costsObj.costFT;
		const totalPriceFT_corr = cocstsObj_corr.costFT;

		// Subtotal price
		let subtotalPrice = costsObj.costTotal;
		let subtotalPrice_corr = cocstsObj_corr.costTotal;

		// Only for sites with BPA fee
		if (data.BPAFee > 0) subtotalPrice = subtotalPrice + data.BPAFee;
		if (data.BPAFee > 0) subtotalPrice_corr = subtotalPrice_corr + data.BPAFee;

		// VAT calculations
		const currency = data.currency;
		const vatPercent = data.vat;
		const vatDisplay = parseNo(data.vat * 100);

		const subtotal_diff = parseFloat(subtotalPrice - subtotalPrice_corr.toFixed(2));
		const { VAT: subtotal_diff_VAT_amount, totalDue: total_diff } = solarCalc.calculateVAT(subtotal_diff, currency, vatPercent);

		// Payment section
		// const paymentSection = buildPaymentSection(bankAccount);

		// Electricity bill section parameters
		// const discountON = parseFloat(formatNumber(discount.ON * 100, 2));
		// const discRemEN = getDiscountRemark("EN", discountON, EAEng[1]);
		// const discRemTH = getDiscountRemark("TH", discountON, EAThai[1]);

		// const param = {
		// 	tariffType,
		// 	tariffDesc,
		// 	peakLabels,
		// 	EAEng,
		// 	EAThai,
		// 	discRemEN,
		// 	discRemTH,
		// 	priceON: priceON_disc,
		// 	data,
		// 	GYTariff_remarks,
		// 	savings,
		// };

		let docDefinition;

		// const locationSection = buildLocationSection(location);

		// below is for new invoice TH template for new envelope design since 08/31/23
		// const custm_envelope = await GYAxios.get(`/invoice/billing_address/${data.siteID}`).then(res => res.data);
		// const mail_bill_add = custm_envelope?.mail_bill_add; // site name and address combined
		// const mail_name = custm_envelope?.mail_name; // name of person (from client side) who receives this invoice
		// const mail_phone_no = custm_envelope?.mail_phone_no;

		// Location section
		const entitySection = buildEntitySection(solarEntity, { isNewInv: true });

		// PDF config
		docDefinition = {
			pageSize: "A4",
			pageMargins: 0,
			content: [
				{ nodeName: 'DIV', style: 'invoiceTHStyle', stack: [
				{
					nodeName: "DIV",
					margin: [ cm_to_pt(2), cm_to_pt(1.9), 0, 0 ],
					columns: [
						{
							nodeName: "DIV",
							width: cm_to_pt(9.5),
							stack: [
								{
									image: logo,
									width: 150,
								},
								...entitySection
							],
						},
						{
							nodeName: "DIV",
							margin: [cm_to_pt(0.5), 0, 0, 0],
							stack: [
								{ text: "Credit Note / Tax Invoice", style: "header_lg_new", margin: [ 0, 0, cm_to_pt(2), 0 ] },
								// { text: copyTypes[copyType], style: "sub_header_2_new", margin: 0 },
								{
									margin: [20, 10],
									table: {
										widths: [cm_to_pt(2.5), '*'],
										body: [
											[
												{
													text: "CREDIT NOTE NO.",
													style: ["boldHeader"],
												},
												{
													text: creditNoteNo,
													style: ["invoice_details"],
												},
											],
											[
												{
													text: "DATE",
													style: ["boldHeader"],
												},
												{ text: '20/10/2023', style: ["invoice_details"] },
											],
											[
												{
													text: "REFERENCE",
													style: ["boldHeader"],
												},
												{
													text: data.invNo,
													style: ["invoice_details"],
												},
											],
											[
												{
													text: "PROJECT",
													style: ["boldHeader"],
												},
												{ text: data.location, style: ["invoice_details"] },
											],

											// [
											// 	{
											// 		text: "Credit Term",
											// 		style: ["boldHeader"],
											// 	},
											// 	{
											// 		text: data.creditTerm + " days",
											// 		style: ["invoice_details"],
											// 	},
											// ],
											// [
											// 	{
											// 		text: "Due date",
											// 		style: ["boldHeader"],
											// 	},
											// 	{ text: data.dueDate, style: ["invoice_details"] },
											// ],
											// locationSection[0],
											// locationSection[1],
										],
									},
									layout: {
										defaultBorder: false,
									},
								}
							]
						},
					],
				},
				{
					nodeName: 'DIV',
					margin: [ cm_to_pt(2), 0 ],
					columns: [
						{
							nodeName: "DIV",
							margin: [20, 10],
							width: '50%',
							stack: [
								{
									text: billingName,
									style: ["boldHeader"],
								},
								{
									text: billingAddress,
								},
								{
									text: `TAX ID : ${data.taxID}`,
								},
							],
						},
					]
				},
				{
					style: "table_main",
					margin: [ cm_to_pt(2), 0 ],
					table: {
						body: [
							[
								{
									text: "DESCRIPTION",
									border: [false, true, false, true],
									style: ["table_header", "boldHeader"],
								},
								{
									border: [false, true, false, true],
									text: "QUANTITY (kWh)",
									alignment: "right",
									style: ["table_header", "boldHeader"],
								},
								{
									border: [false, true, false, true],
									text: "UNIT COST (THB)",
									alignment: "right",
									style: ["table_header", "boldHeader"],
								},
								{
									border: [false, true, false, true],
									text: "TOTAL (THB)",
									alignment: "right",
									style: ["table_header", "boldHeader"],
								},
							],

							// INVALID INFORMATION
							[
								{
									text: `Reference: ${data.invNo}`,
									style: ["table_main_rows", "boldHeader"],

									colSpan: 4,
								},
							],
							[
								{
									text: `Period: ${period}`,
									style: ["table_main_rows", "boldHeader"],

									colSpan: 4,
								},
							],
							[
								{
									text: `Electricity Production during ${peakLabels[0]} Time ${discountON_display}`,
									style: "table_main_rows",
								},
								{
									text: solarON,
									style: "table_main_rows",
									alignment: "right",
								},
								{
									text: priceON_disc,
									style: "table_main_rows",
									alignment: "right",
								},
								{
									text: formatNumber(totalPriceON, 2),
									style: ["table_main_rows", "boldHeader"],
									alignment: "right",
								},
							],
							[
								{
									text: `Electricity Production during ${peakLabels[1]} Time ${discountOFF_display}`,
									style: "table_main_rows",
								},
								{
									text: solarOFF,
									alignment: "right",
									style: "table_main_rows",
								},
								{
									text: priceOFF_disc,
									alignment: "right",
									style: "table_main_rows",
								},
								{
									text: formatNumber(totalPriceOFF, 2),
									alignment: "right",
									style: ["table_main_rows", "boldHeader"],
								},
							],
							!isBTGSite
								? [
									{
										text: `Ft Cost ${discountFT_display}`,
										style: "table_main_rows",
									},
									{
										text: solarTotal,
										alignment: "right",
										style: "table_main_rows",
									},
									{
										text: FTcost_disc,
										alignment: "right",
										style: "table_main_rows",
									},
									{
										text: formatNumber(totalPriceFT, 2),
										alignment: "right",
										style: ["table_main_rows", "boldHeader"],
									},
								]
								: ["", "", "", ""],
							data.BPAFee > 0 ? [
								{
									text: "Basic Power Availability Fee",
									style: "table_main_rows",
									lineHeight: 0,
									height: 0,
								},
								{
									text: "1.00",
									alignment: "right",
									style: "table_main_rows",
									lineHeight: 0,
									height: 0,
								},
								{
									text: formatNumber(data.BPAFee, 2),
									alignment: "right",
									style: "table_main_rows",
									lineHeight: 0,
									height: 0,
								},
								{
									text: formatNumber(data.BPAFee, 2),
									alignment: "right",
									style: ["table_main_rows", "boldHeader"],
									lineHeight: 0,
									height: 0,
								},
							] : null,
							[
								{
									colSpan: 3,
									text: "Subtotal",
									alignment: "right",
									style: ["table_main_rows", "boldHeader"],
								},
								"",
								"",
								{
									text: formatNumber(subtotalPrice, 2),
									alignment: "right",
									style: ["table_main_rows", "boldHeader"],
								},
							],

							['', '', '', ''],

							// CORRECT INFORMATION
							[
								{
									text: `Corrected Information`,
									style: ["table_main_rows", "boldHeader"],

									colSpan: 4,
								},
							],
							[
								{
									text: `Period: ${period}`,
									style: ["table_main_rows", "boldHeader"],

									colSpan: 4,
								},
							],
							[
								{
									text: `Electricity Production during ${peakLabels[0]} Time ${discountON_display}`,
									style: "table_main_rows",
								},
								{
									text: solarON,
									style: "table_main_rows",
									alignment: "right",
								},
								{
									text: priceON_disc,
									style: "table_main_rows",
									alignment: "right",
								},
								{
									text: formatNumber(totalPriceON, 2),
									style: ["table_main_rows", "boldHeader"],
									alignment: "right",
								},
							],
							[
								{
									text: `Electricity Production during ${peakLabels[1]} Time ${discountOFF_display}`,
									style: "table_main_rows",
								},
								{
									text: solarOFF,
									alignment: "right",
									style: "table_main_rows",
								},
								{
									text: priceOFF_disc,
									alignment: "right",
									style: "table_main_rows",
								},
								{
									text: formatNumber(totalPriceOFF, 2),
									alignment: "right",
									style: ["table_main_rows", "boldHeader"],
								},
							],
							!isBTGSite
								? [
									{
										text: `Ft Cost ${discountFT_display}`,
										style: "table_main_rows",
									},
									{
										text: solarTotal,
										alignment: "right",
										style: "table_main_rows",
									},
									{
										text: FTcost_disc_corr,
										alignment: "right",
										style: "table_main_rows",
									},
									{
										text: formatNumber(totalPriceFT_corr, 2),
										alignment: "right",
										style: ["table_main_rows", "boldHeader"],
									},
								]
								: null,
							data.BPAFee > 0 ? [
								{
									text: "Basic Power Availability Fee",
									style: "table_main_rows",
								},
								{
									text: "1.00",
									alignment: "right",
									style: "table_main_rows",
								},
								{
									text: formatNumber(data.BPAFee, 2),
									alignment: "right",
									style: "table_main_rows",
								},
								{
									text: formatNumber(data.BPAFee, 2),
									alignment: "right",
									style: ["table_main_rows", "boldHeader"],
								},
							] : null,
							[
								{
									colSpan: 3,
									text: "Subtotal",
									alignment: "right",
									style: ["table_main_rows", "boldHeader"],
								},
								"",
								"",
								{
									text: formatNumber(subtotalPrice_corr, 2),
									alignment: "right",
									style: ["table_main_rows", "boldHeader"],
								},
							],

							['', '', '', ''],

							// REMARK
							[
								{
									text: "Remark: Due to the retroactive update of the FT cost from 0.6689 to 0.2048 for the period 09/2023 to 12/2023",
									style: "table_main_rows",
									colSpan: 4,
								}
							],
							[
								{
									text: "Refer to Tax invoice No. " + data.invNo,
									style: "table_main_rows",
									colSpan: 4,
								}
							],

							['', '', '', ''], // empty row

							// DIFFERENCE TOTAL
							[
								{
									colSpan: 3,
									text: "Value according to the original invoice",
									alignment: "right",
									style: ["table_main_rows", "boldHeader"],
								},
								"",
								"",
								{
									text: formatNumber(subtotalPrice, 2),
									alignment: "right",
									style: ["table_main_rows", "boldHeader"],
								},
							],
							[
								{
									colSpan: 3,
									text: "Current value",
									alignment: "right",
									style: ["table_main_rows", "boldHeader"],
								},
								"",
								"",
								{
									text: formatNumber(subtotalPrice_corr, 2),
									alignment: "right",
									style: ["table_main_rows", "boldHeader"],
								},
							],
							[
								{
									colSpan: 3,
									text: "Difference",
									alignment: "right",
									style: ["table_main_rows", "boldHeader"],
								},
								"",
								"",
								{
									text: formatNumber(subtotal_diff, 2),
									alignment: "right",
									style: ["table_main_rows", "boldHeader"],
								},
							],
							[
								{
									colSpan: 3,
									text: `VAT ${vatDisplay}%`,
									alignment: "right",
									style: ["table_main_rows", "boldHeader"],
								},
								"",
								"",
								{
									text: formatNumber(subtotal_diff_VAT_amount, 2),
									alignment: "right",
									style: ["table_main_rows", "boldHeader"],
								},
							],
							[
								{
									colSpan: 3,
									text: "TOTAL DUE",
									alignment: "right",
									border: [false, true, false, true],
									style: "boldHeader",
									margin: [0, 1, 0, 0],
								},
								{ text: "", border: [false, true, false, true] },
								{ text: "", border: [false, true, false, true] },
								{
									text: formatNumber(total_diff, 2),
									alignment: "right",
									border: [false, true, false, true],
									style: "boldHeader",
									margin: [0, 1, 0, 0],
								},
							],
						],
						widths: ["53%", "15%", "17%", "15%"],
						// widths: [ cm_to_pt(3), '*', '*', '*' ],
						heights: 9,
					},
					layout: {
						defaultBorder: false,
						paddingLeft: function (i, node) {
							return 0;
						},
						paddingRight: function (i, node) {
							return 0;
						},
						paddingTop: function (i, node) {
							return 0;
						},
						paddingBottom: function (i, node) {
							return 0;
						},
					},
				},
				{
					nodeName: "DIV",
					stack: [
						{
							text: "* This invoice is exempted from income tax under BOI promotion and is therefore not subject to withholding tax.",
							color: "red",
							italic: true,
						},
						{
							text: holidays != "" ? `*Holiday : ${holidays}` : "",
							color: "blue",
						},
						{
							text: data.specialComment != null ? data.specialComment : "",
						},
					],
					margin: [cm_to_pt(2), 4, cm_to_pt(2), 8],
				},
				// {
				// 	nodeName: "DIV",
				// 	margin: [ cm_to_pt(2), 0 ],
				// 	stack: [isBTGSite ? getBTGElectricBillSection(param) : getElectricBillSection(param)]
				// },
				
					// footer moved to page content for easy PDF merging in bulk download (1 click download)
					{
						nodeName: "DIV",
						absolutePosition: { x: 0, y: cm_to_pt(26) },
						stack: [
							// {
							// 	stack: paymentSection,
							// 	margin: [cm_to_pt(2), 0, cm_to_pt(2), cm_to_pt(0.5)],
							// },
							{
								text: [
									"This is a computer-generated credit note. No signature is required"
								],
								style: {
									alignment: "center",
								},
							},
						]
					}
				]},
			],
			footer: [
				/* {
					columns: [
						{
							stack: [
								signature,
								{
									text: "..............................................................................",
									style: "sub_signature",
								},
								{
									text: `Authorized by ${authName}`,
									style: "sub_signature",
								},
							],
							style: "signature",
						},
						{
							stack: [
								{
									text: "..............................................................................",
									style: "sub_signature",
								},
								{
									text: "Received by: ___________________",
									style: "sub_signature",
								},
								{ text: "Date ....../....../......", style: "sub_signature" },
							],
							style: "signature",
						},
					],
					margin: [0, -105, 0, 0],
				}, */
				// {
				// 	stack: paymentSection,
				// 	margin: [40, -190, 0, 0],
				// },
				// {
				// 	stack: [
				// 		{
				// 			text: [
				// 				"This is a computer-generated Invoice. No signature is required"
				// 			],
				// 		},
				// 	],
				// 	margin: [40, 20, 40, 0],
				// 	style: {
				// 		alignment: "center",
				// 	},
				// },
			],
			styles: {
				header_lg_new: {
					fontSize: 13,
					margin: [0, 0, 0, 0],
					alignment: 'right',
				},
				sub_header: {
					decoration: "underline",
				},
				sub_header_2_new: {
					fontSize: 10,
					color: "#aaa",
					font: "Trirong",
					margin: [0, -3, 0, 0],
					alignment: 'right',
				},
				address_window_text: {
					fontSize: 8,
					font: "Trirong",
					margin: 0
				},
				address_window_send_to: {
					bold: true,
				},
				table_header: {
					margin: [0, 1, 0, 0],
					fontSize: 10,
				},
				table_main: {
					margin: [0, 5, 0, 0],
				},
				table_main_rows: {
					margin: 0,
				},
				boldHeader: { bold: true },
				savings: {
					color: "green",
					bold: true,
				},
				signature: {
					alignment: "center",
				},
				sub_signature: {
					margin: [0, 5, 0, 0],
				},
				thai: {
					font: "Trirong",
					fontSize: 8,
					margin: [0, 0, 0, -3],
				},
				invoiceTHStyle: {
					font: "Calibri",
					fontSize: 9,
				}					
			},
			defaultStyle: {
				font: "Calibri",
				fontSize: 9,
			},
		};

		// remove empty doc definition content
		const removeEmpty = obj => {
			const keys = [ 'content', 'body', 'stack', 'columns' ];
			for(let key of keys) {
				if(Object.keys(obj).includes(key) && Array.isArray(obj[key])) {
					obj[key] = obj[key].filter(item => item).map(item => removeEmpty(item));
				}
			}
			if(Object.keys(obj).includes('table')) {
				obj.table.body = obj.table.body.filter(item => item).map(item => removeEmpty(item));
			}
			return obj;
		}
		docDefinition = removeEmpty(docDefinition);

		onDocDefinition(clone(docDefinition));

		// Generate PDFMAKE document
		const pdfDocGenerator = pdfMake.createPdf(docDefinition);

		// Set PDFMAKE config for download in the later stage
		setPDFDoc(docDefinition);

		if (PDF == null && selectedSite != null) {
			// Get dataURL of document for download
			pdfDocGenerator.getDataUrl((dataUrl) => {
				setPDF(dataUrl);
			});
		}
	};

	/**
	 * Download PDF document
	 * @returns PDF document
	 */
	const downloadPDF = () =>
		pdfMake.createPdf(PDFDoc).download(`${creditNotePDFName}.pdf`);

	useMemo(() => {
		const labelsAreReady = !peakLabels.includes("");

		if (props.selectedSite != null && labelsAreReady)
			generateInvoice(props.selectedSite);

		return () => setCreditNotePDFName(null);
	}, [props.selectedSite, peakLabels]);

	return (
		<>
			{PDF != null ? (
				<>
					<iframe style={{ width: "98%", height: "72vh" }} src={PDF} />
					<Row>
						<Button onClick={downloadPDF}>Download</Button>
					</Row>
				</>
			) : (
				<LoaderGraph style={{ height: "72vh" }} />
			)}
		</>
	);
}

export default CreditNoteTemplate_TH;
